// ReasonField — a reason textarea that nests an info bar inside a shared
// border (.ra-reason-field / .ra-reason-field__bar) when the resource requires
// a reason. When not required it is just the plain DS Textarea. The error
// variant (red border + red bar) is only ever passed true after the field has
// been touched, so validation stays lazy.

import { Box, Flex, Icon } from "@chakra-ui/react";
import { TbInfoCircle } from "react-icons/tb";
import Textarea from "components/ds/Textarea";

export interface ReasonFieldProps {
  /** When false, renders a plain Textarea (no info bar). */
  required: boolean;
  /** Red error treatment. Pass true only once the field has been touched. */
  error: boolean;
  value: string;
  onChange: (next: string) => void;
  onBlur?: () => void;
  /** Copy shown in the info bar (varies between bulk and per-item). */
  barText: string;
  placeholder?: string;
  rows?: number;
}

const ReasonField = ({
  required,
  error,
  value,
  onChange,
  onBlur,
  barText,
  placeholder,
  rows = 3,
}: ReasonFieldProps) => {
  if (!required) {
    return (
      <Textarea
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  return (
    <Box
      borderWidth="1px"
      borderStyle="solid"
      borderRadius="component.lg"
      borderColor={error ? "error.400" : "neutral.300"}
      bg="neutral.25"
      overflow="hidden"
      transition="border-color 120ms, box-shadow 120ms"
      _focusWithin={{
        borderColor: error ? "error.400" : "p.500",
        boxShadow: "focusRing",
      }}
    >
      <Flex
        align="flex-start"
        gap="2xs"
        px="md"
        py="sm"
        bg={error ? "error.50" : "warning.50"}
        color={error ? "error.700" : "warning.800"}
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor={error ? "error.200" : "warning.200"}
        fontSize="xs"
        lineHeight="1.4"
      >
        <Icon as={TbInfoCircle} boxSize="14px" mt="1px" flexShrink={0} />
        <Box as="span">{barText}</Box>
      </Flex>
      <Textarea
        rows={rows}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        // Strip the textarea's own chrome — the wrapper owns the border/ring.
        border="none"
        borderRadius={0}
        bg="transparent"
        boxShadow="none"
        minH="56px"
        _hover={{ bg: "transparent" }}
        _focus={{ boxShadow: "none", borderColor: "transparent" }}
        _focusVisible={{ boxShadow: "none", borderColor: "transparent" }}
      />
    </Box>
  );
};

export default ReasonField;
